import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Mail, MailSearch, SendHorizonal, Inbox, MessageSquare, FileSignature, ExternalLink } from "lucide-react";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/lib/authContext";
import { api, emailApi } from "@/lib/api";
import { useHiddenMailboxes } from "@/lib/userPrefs";
import { filterHiddenGroups } from "@/lib/email/hiddenMailboxes";
import { HeaderSplitNav, type HeaderSplitNavEntry } from "./HeaderSplitNav";

// Routes this nav item covers — used to highlight the main button as active.
const EMAIL_ROUTES = ["/inbox", "/email-search", "/client-email-log", "/client-responses", "/messages", "/signatures"];

export function EmailNav() {
  const { user } = useAuth();
  const role = user?.role || "staff";

  const { data: allGroups = [] } = useQuery({
    queryKey: ["inbox"],
    queryFn: () => emailApi.getInbox(),
    refetchInterval: 60_000,
  });
  const [hiddenMailboxes] = useHiddenMailboxes();
  const groups = useMemo(() => filterHiddenGroups(allGroups, hiddenMailboxes), [allGroups, hiddenMailboxes]);
  const unreadCount = groups.reduce((sum, g) => sum + (g.unread ?? 0), 0);

  const { data: smsConvos = [] } = useQuery({
    queryKey: ["sms-conversations"],
    queryFn: () => api.getSmsConversations(),
    refetchInterval: 30_000,
    staleTime: 20_000,
  });
  const smsUnreadTotal = (smsConvos as any[]).reduce((sum: number, c: any) => sum + (c.unread_count ?? 0), 0);

  const entries: HeaderSplitNavEntry[] = [
    { label: "Shared Inbox", href: "/inbox", icon: Mail, roles: ["staff", "manager", "admin", "contractor"] },
    { label: "Email Search", href: "/email-search", icon: MailSearch, roles: ["staff", "manager", "admin", "contractor"] },
    { label: "Sent Email Log", href: "/client-email-log", icon: SendHorizonal, roles: ["staff", "manager", "admin"] },
    { label: "Portal Responses", href: "/client-responses", icon: Inbox, roles: ["staff", "manager", "admin"] },
    { label: "Text Messages", href: "/messages", icon: MessageSquare, badge: smsUnreadTotal > 0 ? smsUnreadTotal : undefined },
    { label: "Signatures", href: "/signatures", icon: FileSignature },
  ].filter((e) => !e.roles || e.roles.includes(role));

  return (
    <HeaderSplitNav
      label="Email"
      icon={Mail}
      href="/inbox"
      entries={entries}
      badge={unreadCount}
      activeRoutes={EMAIL_ROUTES}
      testId="email"
      extraItems={
        <DropdownMenuItem
          onClick={() =>
            window.open("/inbox?popout=1", "centcom-inbox", "width=520,height=900,menubar=no,toolbar=no")
          }
        >
          <ExternalLink className="w-4 h-4 mr-2" />
          Open inbox in pop-out window
        </DropdownMenuItem>
      }
    />
  );
}
